import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

// APIs
import { GetDepartments } from "../../../api/masters/departmentAPI";
import { GetStaffById, UpdateStaff } from "../../../api/masters/staffAPI";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// ✅ Validation Schema (aligned with your Mongoose model)
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  departmentId: yup.string().optional(),
  nature: yup.string().required("Nature is required"),
  address: yup.string().optional(),
  mobileNo: yup.string(),
  phoneNo: yup.string().optional()
});

const EditStaff = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);

  const { id } = useParams();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  const fetchData = async () => {
    try {
      const response = await GetStaffById(id); // Make sure login function returns token

      if (response.success) {
        setValue("name", response.data.name); // Set default values
        setValue("departmentId", response.data.departmentId);
        setValue("nature", response.data.nature);
        setValue("address", response.data.address);
        setValue("mobileNo", response.data.mobileNo);
        setValue("phoneNo", response.data.phoneNo);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(
    () => {
      const loadData = async () => {
        try {
          dispatch({ type: "LOADER_START" });

          await fetchDepartments();
          await fetchData();
        } catch (error) {
          showToast("error", error);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      };

      loadData();
    },
    [id]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const res = await UpdateStaff(id, data);
      if (res.success) {
        showToast("success", res.message);
      } else {
        showToast("error", res.message);
      }
    } catch (err) {
      showToast("error", err.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Staff" title="Add Staff" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Staff</h5>
                <Link to={`/masters/staff/list`}>
                  <button className="btn btn-primary" id="list">
                    List Staff
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Staff
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Name *</label>
                      <input
                        className="form-control"
                        placeholder="Staff Name"
                        {...register("name")}
                      />
                      {errors.name &&
                        <p className="text-danger">
                          {errors.name.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Department</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dep =>
                          <option key={dep._id} value={dep._id}>
                            {dep.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Nature *</label>
                      <select
                        className="form-control"
                        placeholder="Nature"
                        {...register("nature")}
                      >
                        <option value="">Select Nature</option>
                        <option value="OT Technician">OT Technician</option>
                        <option value="OT Assistant">OT Assistant</option>
                        <option value="Others">Others</option>
                      </select>
                      {errors.nature &&
                        <p className="text-danger">
                          {errors.nature.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Address</label>
                      <textarea
                        className="form-control"
                        placeholder="Address"
                        {...register("address")}
                      />
                      {errors.address &&
                        <p className="text-danger">
                          {errors.address.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Mobile No</label>
                      <input
                        className="form-control"
                        {...register("mobileNo")}
                      />
                      {errors.mobileNo &&
                        <p className="text-danger">
                          {errors.mobileNo.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Phone No</label>
                      <input
                        className="form-control"
                        {...register("phoneNo")}
                      />
                      {errors.phoneNo &&
                        <p className="text-danger">
                          {errors.phoneNo.message}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditStaff;
